import React, { useEffect, useRef, useState } from 'react';
import {
  Button, IconButton, Dialog, Field, TextInput, Banner, CkThemeProvider, useCkInside, useCkToast, ckConfirm,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { KV, apiError } from '../../next/ui';

const EMAIL_RE = /^[^\s<>@,;]+@[^\s<>@,;]+\.[^\s<>@,;]+$/;
const loginUrl = () => `${window.location.origin}/login`;
const credentialsText = (username, password) => `שם משתמש: ${username}\nסיסמה: ${password}\nכתובת כניסה: ${loginUrl()}`;

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // ללא הרשאת clipboard (http / דפדפן ישן)
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  }
}

function CopyValue({ value, label, onCopied }) {
  const toast = useCkToast();
  const [copied, setCopied] = useState(false);
  const timer = useRef();
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    if (!(await copyText(value))) { toast.error('לא ניתן להעתיק אוטומטית. סמנו את הטקסט והעתיקו ידנית'); return; }
    setCopied(true);
    onCopied?.();
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1800);
  };
  return (
    <span className="nx-row" style={{ gap: 6, alignItems: 'center' }}>
      <code dir="ltr" style={{ fontSize: 16, fontWeight: 600, userSelect: 'all' }}>{value}</code>
      <IconButton icon={copied ? 'check' : 'copy'} label={copied ? 'הועתק' : `העתקת ${label}`} onClick={copy} />
    </span>
  );
}

/**
 * פרטי כניסה אחרי יצירת משתמש / איפוס סיסמה — הסיסמה מוצגת כאן בלבד.
 * העתקה (כל שדה או הכל יחד) ושליחה במייל דרך מסך אישור.
 * credentials: { user, password, reason: 'create' | 'reset' } | null
 * עובד גם במסך הישן (בלי ספקי crm-kit) — עוטף את עצמו ב-CkThemeProvider, והמשוב מוצג בדיאלוג עצמו.
 */
export default function CredentialsDialog(props) {
  const inside = useCkInside();
  if (!props.credentials) return null;
  const body = <CredentialsDialogBody {...props} />;
  return inside ? body : <CkThemeProvider>{body}</CkThemeProvider>;
}

function CredentialsDialogBody({ credentials, onClose, onEmailSaved }) {
  const toast = useCkToast();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('details');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [handled, setHandled] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const copiedTimer = useRef();
  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const user = credentials?.user;
  const password = credentials?.password || '';
  useEffect(() => {
    if (!credentials) return;
    setEmail(credentials.user?.email || '');
    setStep('details');
    setError('');
    setSentTo('');
    setHandled(false);
    setCopiedAll(false);
  }, [credentials]);

  const emailOk = EMAIL_RE.test(email.trim());

  const close = async () => {
    if (!handled && !(await ckConfirm({
      title: 'סגירת פרטי הכניסה',
      message: 'הפרטים לא הועתקו ולא נשלחו. אחרי הסגירה לא ניתן לראות את הסיסמה שוב — רק לאפס אותה. לסגור?',
      confirmLabel: 'סגירה',
    }))) return;
    onClose();
  };

  const copyAll = async () => {
    if (!(await copyText(credentialsText(user.username, password)))) { toast.error('לא ניתן להעתיק אוטומטית. סמנו את הטקסט והעתיקו ידנית'); return; }
    setHandled(true);
    setCopiedAll(true);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopiedAll(false), 1800);
    toast.success('שם המשתמש והסיסמה הועתקו');
  };

  const send = async () => {
    setSending(true);
    setError('');
    try {
      const to = email.trim().toLowerCase();
      const res = await api.post(`/users/${user._id}/send-credentials`, { password, email: to });
      setSentTo(res.data.to);
      setHandled(true);
      setStep('details');
      if (res.data.to !== (user.email || '')) onEmailSaved?.();
      toast.success(`פרטי הכניסה נשלחו אל ${res.data.to}`);
    } catch (e) {
      setError(apiError(e, 'שליחת המייל נכשלה'));
      setStep('details');
    } finally {
      setSending(false);
    }
  };

  const confirming = step === 'confirm';
  return (
    <Dialog
      open
      onClose={close}
      title={confirming ? 'אישור שליחה במייל' : `פרטי כניסה — ${user.username}`}
      icon={confirming ? 'mail' : 'key'}
      size="md"
      footer={confirming ? (
        <>
          <Button variant="ghost" disabled={sending} onClick={() => setStep('details')}>חזרה</Button>
          <Button variant="primary" icon="mail" disabled={sending} onClick={send}>{sending ? 'שולח…' : 'אישור ושליחה'}</Button>
        </>
      ) : (
        <>
          <Button variant="ghost" onClick={close}>סגירה</Button>
          <Button icon={copiedAll ? 'check' : 'copy'} onClick={copyAll}>{copiedAll ? 'הועתק' : 'העתקת שם משתמש וסיסמה'}</Button>
          <Button variant="primary" icon="mail" disabled={!emailOk} onClick={() => { setError(''); setStep('confirm'); }}>שליחה במייל…</Button>
        </>
      )}
    >
      {confirming ? (
        <div className="nx-stack">
          <p style={{ margin: 0 }}>לשלוח את פרטי הכניסה במייל אל הכתובת הבאה?</p>
          <KV items={[
            { label: 'נמען', value: email.trim().toLowerCase(), ltr: true },
            { label: 'שם משתמש', value: user.username, ltr: true },
            { label: 'סיסמה', value: password, ltr: true },
            { label: 'כתובת כניסה', value: loginUrl(), ltr: true },
          ]}
          />
          {email.trim().toLowerCase() !== (user.email || '') ? (
            <Banner tone="info" icon="info">הכתובת תישמר גם בכרטיס המשתמש.</Banner>
          ) : null}
        </div>
      ) : (
        <div className="nx-stack">
          {error ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}
          {sentTo ? (
            <Banner tone="success" icon="check">פרטי הכניסה נשלחו אל {sentTo}</Banner>
          ) : (
            <Banner tone="warning" icon="alert">
              {credentials.reason === 'reset' ? 'הסיסמה אופסה.' : 'המשתמש נוצר.'} הסיסמה מוצגת עכשיו בלבד — העתיקו אותה או שלחו במייל לפני הסגירה.
            </Banner>
          )}
          <KV items={[
            { label: 'שם משתמש', value: <CopyValue value={user.username} label="שם משתמש" onCopied={() => setHandled(true)} /> },
            { label: 'סיסמה', value: <CopyValue value={password} label="סיסמה" onCopied={() => setHandled(true)} /> },
          ]}
          />
          <Field label="מייל לשליחה" hint={emailOk || !email.trim() ? 'הפרטים יישלחו מדומיין השליחה של המערכת' : 'כתובת מייל לא תקינה'}>
            <TextInput ltr type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
          </Field>
        </div>
      )}
    </Dialog>
  );
}
